import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { DocNode } from '@atlaskit/adf-schema';
import type { ChangeEvent } from '@atlassian/jira-issue-view-model/src/change-type.tsx';
import type { Issue } from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import type { FieldConfigs } from '../../../../common/hooks/use-store-fields/types.tsx';
import type {
	Card,
	CardId,
	GlobalIssueCreateProps,
	CardIdentifiers,
} from '../../../../model/types.tsx';

import type { CardCreateGql } from '../../../../rest/card-create-multiple-gql/types.tsx';
import type { EpicIssueTypeState } from '../../../reducers/config/epic/types.tsx';
import {
	beginOptimisticUiMeta,
	commitOptimisticUiMeta,
	revertOptimisticUiMeta,
	type OptimisticId,
	type OptimisticUiMeta,
} from '../../meta/optimistic-ui-meta.tsx';

export const CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC =
	'state.actions.cards.create.CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC' as const;
export const CARD_CREATE_MULTIPLE_REQUEST =
	'state.actions.cards.create.CARD_CREATE_MULTIPLE_REQUEST' as const;
export const CARD_CREATE_SUCCESS = 'state.actions.cards.create.CARD_CREATE_SUCCESS' as const;
export const CARD_CREATE_FAILURE = 'state.actions.cards.create.CARD_CREATE_FAILURE' as const;
export const CARD_CREATE_FILTERED = 'state.actions.cards.create.CARD_CREATE_FILTERED' as const;
export const CARD_CREATE_FILTERED_V2 =
	'state.actions.cards.create.CARD_CREATE_FILTERED_V2' as const;
export const CARD_CREATE_UNMAPPED = 'state.actions.cards.create.CARD_CREATE_UNMAPPED' as const;
export const CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK =
	'state.actions.cards.create.CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK' as const;
export const CARD_CREATE_GLOBAL = 'state.actions.cards.create.CARD_CREATE_GLOBAL' as const;
export const CARD_CREATE_SPLIT_ISSUE =
	'state.actions.cards.create.CARD_CREATE_SPLIT_ISSUE' as const;

// CARD CREATE MULTIPLE
export type CardCreateMultipleRequestAction = {
	type: typeof CARD_CREATE_MULTIPLE_REQUEST;
	payload: {
		summaries: string[];
		typeId: number;
		parentId: number | null;
		fixVersions: string[];
		insertBefore: CardId | null;
		listId: string;
		typeIcon: string;
		tempIds: number[];
		descriptions: (DocNode | string)[] | null;
		storeFieldsFromCardCreateData: (
			cardCreateData: CardCreateGql,
			fieldConfigs: FieldConfigs,
		) => void;
		storeFieldsFromFetchIssuesData: (
			issues: Issue[] | null,
			epicIssueType: EpicIssueTypeState | null,
		) => void;
	};
	meta: {
		analyticsEvent?: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardCreateMultipleRequest = ({
	tempIds,
	summaries,
	typeId,
	typeIcon,
	parentId,
	fixVersions,
	insertBefore = null,
	listId,
	storeFieldsFromCardCreateData,
	storeFieldsFromFetchIssuesData,
	analyticsEvent,
	descriptions = null,
}: {
	tempIds: number[];
	summaries: string[];
	typeId: number;
	parentId: number | null;
	typeIcon: string;
	fixVersions: string[];
	insertBefore: CardId | null;
	listId: string;
	descriptions?: (DocNode | string)[] | null;
	storeFieldsFromCardCreateData: (
		cardCreateData: CardCreateGql,
		fieldConfigs: FieldConfigs,
	) => void;
	storeFieldsFromFetchIssuesData: (
		issues: Issue[] | null,
		epicIssueType: EpicIssueTypeState | null,
	) => void;
	analyticsEvent?: UIAnalyticsEvent;
}): CardCreateMultipleRequestAction => ({
	type: CARD_CREATE_MULTIPLE_REQUEST,
	payload: {
		tempIds,
		summaries,
		typeId,
		parentId,
		typeIcon,
		fixVersions,
		insertBefore,
		listId,
		storeFieldsFromCardCreateData,
		storeFieldsFromFetchIssuesData,
		descriptions,
	},
	meta: {
		...beginOptimisticUiMeta(),
		analyticsEvent,
	},
});

export type CardCreateEndOfBacklogOptimisticAction = {
	type: typeof CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC;
	payload: {
		changeEvent: ChangeEvent;
	};
};

export const cardCreateEndOfBacklogOptimistic = (
	changeEvent: ChangeEvent,
): CardCreateEndOfBacklogOptimisticAction => ({
	type: CARD_CREATE_END_OF_BACKLOG_OPTIMISTIC,
	payload: {
		changeEvent,
	},
});

export type CardCreateSuccessAction = {
	type: typeof CARD_CREATE_SUCCESS;
	payload: {
		card: Card;
		listId: string;
		temporaryCardId: CardId;
		createdWithRank: boolean;
		createdWithBulk: boolean;
	};
	meta: {
		analyticsEvent?: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardCreateSuccess = (
	card: Card,
	listId: string,
	temporaryCardId: CardId,
	createdWithRank: boolean,
	createdWithBulk: boolean,
	optimisticId: OptimisticId,
	analyticsEvent?: UIAnalyticsEvent,
): CardCreateSuccessAction => ({
	type: CARD_CREATE_SUCCESS,
	payload: {
		card,
		listId,
		temporaryCardId,
		createdWithRank,
		createdWithBulk,
	},
	meta: {
		...commitOptimisticUiMeta(optimisticId),
		analyticsEvent,
	},
});

export type CardCreateFailureAction = {
	type: typeof CARD_CREATE_FAILURE;
	meta: OptimisticUiMeta;
	payload: {
		errorMessage: string;
		numCards: number;
		statusCode: number | null;
		isClientFetchError: boolean;
	};
};

export const cardCreateFailure = (
	numCards: number,
	optimisticId: OptimisticId,
	errorMessage: string,
	statusCode: number | null,
	isClientFetchError = false,
	analyticsEvent?: UIAnalyticsEvent,
): CardCreateFailureAction => ({
	type: CARD_CREATE_FAILURE,
	meta: {
		...revertOptimisticUiMeta(optimisticId),
		analyticsEvent,
	},
	payload: {
		numCards,
		errorMessage,
		statusCode,
		isClientFetchError,
	},
});

export type CardCreateFilteredAction = {
	type: typeof CARD_CREATE_FILTERED;
	payload: Card;
};

export const cardCreateFiltered = (card: Card): CardCreateFilteredAction => ({
	type: CARD_CREATE_FILTERED,
	payload: card,
});

export type CardCreateFilteredV2Action = {
	type: typeof CARD_CREATE_FILTERED_V2;
	payload: CardId[];
};

export const cardCreateFilteredV2 = (cardIds: CardId[]): CardCreateFilteredV2Action => ({
	type: CARD_CREATE_FILTERED_V2,
	payload: cardIds,
});

export type CardCreateUnmappedAction = {
	type: typeof CARD_CREATE_UNMAPPED;
	payload: {
		numIssues: number;
		cardKeys: string[] | null;
		issueTypeName: string | null;
	};
	meta: {
		analyticsEvent?: UIAnalyticsEvent;
	} & OptimisticUiMeta;
};

export const cardCreateUnmapped = (
	numIssues: number,
	cardKeys: string[] | null,
	optimisticId: OptimisticId,
	issueTypeName: string | null,
	analyticsEvent?: UIAnalyticsEvent,
): CardCreateUnmappedAction => ({
	type: CARD_CREATE_UNMAPPED,
	payload: {
		numIssues,
		cardKeys,
		issueTypeName,
	},
	meta: {
		...revertOptimisticUiMeta(optimisticId),
		analyticsEvent,
	},
});

export type CardCreateUnmappedWithIssueLinkAction = {
	type: typeof CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK;
	payload: {
		cardsIdentifiers: CardIdentifiers[];
	};
};

export const cardCreateUnmappedWithIssueLink = (cardsIdentifiers: CardIdentifiers[]) => ({
	type: CARD_CREATE_UNMAPPED_WITH_ISSUE_LINK,
	payload: {
		cardsIdentifiers,
	},
});

export type CardCreateGlobalAction = {
	type: typeof CARD_CREATE_GLOBAL;
	payload: {
		globalIssues: GlobalIssueCreateProps[];
	};
};

export const cardCreateGlobal = (
	globalIssues: GlobalIssueCreateProps[],
): CardCreateGlobalAction => ({
	type: CARD_CREATE_GLOBAL,
	payload: {
		globalIssues,
	},
});

export type SplitIssueCreateAction = {
	type: typeof CARD_CREATE_SPLIT_ISSUE;
	payload: {
		originalIssueKey: string;
		newIssueKeys: string[];
		issueTypeName: string;
	};
};

export const splitIssueCreate = (
	originalIssueKey: string,
	newIssueKeys: string[],
	issueTypeName: string,
): SplitIssueCreateAction => ({
	type: CARD_CREATE_SPLIT_ISSUE,
	payload: {
		originalIssueKey,
		newIssueKeys,
		issueTypeName,
	},
});

export type Action =
	| CardCreateSuccessAction
	| CardCreateFailureAction
	| CardCreateFilteredAction
	| CardCreateFilteredV2Action
	| CardCreateEndOfBacklogOptimisticAction
	| CardCreateUnmappedAction
	| CardCreateUnmappedWithIssueLinkAction
	| CardCreateMultipleRequestAction
	| CardCreateGlobalAction
	| SplitIssueCreateAction;
